/**
 * Payment Complete Page
 * 
 * This page is shown after Stripe Checkout completes in a new tab.
 * It automatically closes itself so the user returns to the original page
 * where the payment dialog is polling for completion.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2 } from 'lucide-react';

export function PaymentCompletePage() {
  const { t } = useTranslation();
  const [closing, setClosing] = useState(false);
  const [canClose, setCanClose] = useState(true);

  useEffect(() => {
    // Try to close the window after a short delay
    const timer = setTimeout(() => {
      setClosing(true);
      
      // Attempt to close the window
      try {
        window.close();
        
        // If window.close() didn't work (some browsers block it),
        // show a message asking user to close manually
        setTimeout(() => {
          setCanClose(false);
        }, 500);
      } catch {
        setCanClose(false);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="payment-complete-card max-w-md w-full text-center space-y-6">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground">
          {t('payment.paymentSuccessful', 'Payment Successful!')}
        </h1>

        {/* Message */}
        <div className="space-y-2">
          {closing && canClose ? (
            <>
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('payment.closingWindow', 'Closing this window...')}</span>
              </div>
            </>
          ) : !canClose ? (
            <p className="text-muted-foreground">
              {t('payment.closeTabManually', 'You can now close this tab and return to the previous page.')}
            </p>
          ) : (
            <p className="text-muted-foreground">
              {t('payment.processingComplete', 'Your payment has been processed.')}
            </p>
          )}
        </div>

        {/* Visual hint */}
        {!canClose && (
          <div className="pt-4">
            <p className="text-sm text-muted-foreground/70">
              {t('payment.creditsAddedSoon', 'Your credits will appear in your balance momentarily.')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

